import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AgentModelsResponse } from '@tabbio/types';
import { AGENT_MODEL_AUTO } from '@tabbio/utils/agent-models';
import { api } from '../lib/api';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  readAgentModelPreference,
  setAgentModelPreference,
  subscribeAgentModelPreference,
} from '../lib/agent-model-preference';
import { useWorkspace } from '../providers/useWorkspace';
import { useSubscriptionState } from '../providers/useSubscriptionState';

/**
 * Which models this user can pick right now. Availability is a server concern
 * (which provider keys are configured) and so is `locked` (which plan they're
 * on), so the catalog is never filtered client-side.
 */
export function useAgentModels() {
  const { mode, activeWorkspaceId } = useWorkspace();
  const { snapshot } = useSubscriptionState();

  return useQuery({
    queryKey: [
      ...QUERY_KEYS.AGENT_MODELS(mode),
      activeWorkspaceId,
      snapshot?.tier,
      snapshot?.seat?.tier,
      snapshot?.modelPolicy,
    ],
    queryFn: async () => {
      const res = await api.get<AgentModelsResponse>(
        `/api/agent/models?mode=${mode}`,
        { headers: { 'x-agent-mode': mode } },
      );
      return res.data ?? { models: [], defaultId: AGENT_MODEL_AUTO };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * The current selection, kept in sync across every mounted composer.
 *
 * `useSyncExternalStore` rather than local state: the hero composer and the
 * tray composer can both be mounted, and picking in one has to move the other.
 */
export function useAgentModelPreference() {
  const selected = useSyncExternalStore(
    subscribeAgentModelPreference,
    readAgentModelPreference,
    () => AGENT_MODEL_AUTO, // server render has no storage
  );

  const select = useCallback((id: string) => {
    setAgentModelPreference(id);
  }, []);

  return { selected, select };
}

/**
 * Guard against a selection the server would silently decline — a plan
 * downgrade, or a provider key being pulled. Reverting locally keeps the
 * picker honest about what will actually run.
 */
export function useAgentModelReconciliation() {
  const { data } = useAgentModels();
  const { selected, select } = useAgentModelPreference();

  useEffect(() => {
    if (!data) return;
    if (selected === AGENT_MODEL_AUTO) return;
    const entry = data.models.find((model) => model.id === selected);
    if (!entry || entry.locked) {
      select(AGENT_MODEL_AUTO);
    }
  }, [data, select, selected]);
}
